import classNames from 'classnames';
import {observable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import React, {Component} from 'react';

import {Breakpoint, withBreakpoint} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {CarouselSlide, CarouselWithFocusOrder} from '@udemy/react-structure-components';

import {Topics} from 'src/features/featured-topics/types';

import {FeaturedTopicCard} from '../featured-topic-card/featured-topic-card.react-component';
import {FeaturedTopicsUnitStore} from './featured-topics-unit.mobx-store';
import styles from './featured-topics-unit.module.less';

export interface FeaturedTopicsUnitProps {
    displaySeeAllButton: boolean;
    breakpoint: Breakpoint;
    pageType: string;
}

interface FeaturedTopicsUnitClassProps extends FeaturedTopicsUnitProps {
    gettext: (text: string) => string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withGetText = (WrappedComponent: any) => (props: any) => {
    const {gettext} = useI18n();
    return <WrappedComponent {...props} gettext={gettext} />;
};

@observer
class FeaturedTopicsUnitClass extends Component<FeaturedTopicsUnitClassProps> {
    static defaultProps = {
        displaySeeAllButton: true,
        breakpoint: null,
    };

    componentDidMount() {
        runInAction(() => {
            this.isLoading = false;
        });
    }

    @observable isLoading = true;
    store: FeaturedTopicsUnitStore = new FeaturedTopicsUnitStore();

    render() {
        const breakpoint: Breakpoint = this.props.breakpoint || 'lg';

        const carouselOptions = {
            showPager: false,
            allowScroll: false,
            'data-testid': 'featured-topics-unit-carousel',
        };

        if (breakpoint === 'sm') {
            carouselOptions.showPager = false;
            carouselOptions.allowScroll = true;
        } else if (breakpoint === 'md') {
            carouselOptions.showPager = true;
            carouselOptions.allowScroll = true;
        } else {
            carouselOptions.showPager = true;
            carouselOptions.allowScroll = false;
        }

        const {gettext} = this.props;
        const featuredTopics: Topics = this.store.topics;
        const itemClassName = classNames({
            [styles['featured-topic']]: true,
        });
        const slides = featuredTopics.map((topicItem, i) => {
            const titleId = `featured-topic-title-${i}`;
            return (
                <CarouselSlide key={i} aria-labelledby={titleId}>
                    <FeaturedTopicCard
                        topic={topicItem}
                        titleId={titleId}
                        className={itemClassName}
                        pageType={this.props.pageType}
                        isLoading={this.isLoading}
                    />
                </CarouselSlide>
            );
        });
        return (
            <div data-purpose="feature-topics-container">
                <CarouselWithFocusOrder
                    {...carouselOptions}
                    className={classNames(
                        styles['featured-topic-carousel'],
                        styles['lcp-candidate'],
                    )}
                    smallGrid={true}
                    slides={slides}
                    ariaLabel={gettext('Featured topics')}
                    ariaRoleDescription={gettext('carousel')}
                />
            </div>
        );
    }
}

export const FeaturedTopicsUnit = withBreakpoint(withGetText(FeaturedTopicsUnitClass));
